import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useReadOnly } from '../context/ReadOnlyContext';
import { Activity, FileText, Pill, BookOpen, Bell, Download, LayoutDashboard, LogOut, Menu, X, User, Shield, GraduationCap, Apple, Sparkles, Heart, HelpCircle, Compass, BookMarked, Newspaper, ChevronDown, ChevronRight, Map, Lightbulb, Rss, TrendingUp } from 'lucide-react';
import Footer from './Footer';
import { getNavColors } from '../config/navigationColors';
import { getUnreadNotificationCount } from '../utils/api';
import ShareMyCBCModal from './ShareMyCBCModal';

const SHARE_PROMPT_LOGIN_THRESHOLD = 7;
const SHARE_PROMPT_RESHOW_DAYS = 90; // 3 months
const MAX_SHARE_PROMPTS = 3; // Maximum times to show the prompt

const Layout = ({ children }) => {
  const { user, logout, updateUser } = useAuth();
  const { isReadOnly } = useReadOnly();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState([]); // Collapsed by default
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadNewsCount, setUnreadNewsCount] = useState(0);
  const [showSharePrompt, setShowSharePrompt] = useState(false);
  const sidebarRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileMenuScrollPos = useRef(0);

  // Check if we should show the share prompt
  useEffect(() => {
    if (user && !isReadOnly) {
      const loginCount = user.login_count || 0;
      const hasDismissed = user.has_dismissed_share_prompt || false;
      const dismissedAt = user.share_prompt_dismissed_at;
      const promptCount = user.share_prompt_count || 0;
      
      // Don't show if max prompts reached
      if (promptCount >= MAX_SHARE_PROMPTS) {
        return;
      }
      
      let shouldShow = false;
      
      if (!hasDismissed && loginCount >= SHARE_PROMPT_LOGIN_THRESHOLD) {
        // First time showing - after 7 logins
        shouldShow = true;
      } else if (hasDismissed && dismissedAt) {
        // Check if 3 months have passed since dismissal
        const dismissedDate = new Date(dismissedAt);
        const now = new Date();
        const daysSinceDismissal = Math.floor((now - dismissedDate) / (1000 * 60 * 60 * 24));
        
        if (daysSinceDismissal >= SHARE_PROMPT_RESHOW_DAYS) {
          shouldShow = true;
        }
      }
      
      if (shouldShow && !location.pathname.startsWith('/admin')) {
        // Small delay so it doesn't pop up immediately on page load
        const timer = setTimeout(() => {
          setShowSharePrompt(true);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [user, isReadOnly]);

  const handleDismissSharePrompt = () => {
    // Update local user state
    if (updateUser) {
      updateUser({ 
        has_dismissed_share_prompt: true,
        share_prompt_dismissed_at: new Date().toISOString(),
        share_prompt_count: (user?.share_prompt_count || 0) + 1
      });
    }
    setShowSharePrompt(false);
  };

  // Save scroll position and close mobile menu
  const closeMobileMenu = () => {
    if (mobileMenuRef.current) {
      mobileMenuScrollPos.current = mobileMenuRef.current.scrollTop;
    }
    setMobileMenuOpen(false);
  };

  // Add/remove class to body when mobile menu is open (to hide sticky elements)
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
      // Restore scroll position when menu opens
      setTimeout(() => {
        if (mobileMenuRef.current) {
          mobileMenuRef.current.scrollTop = mobileMenuScrollPos.current;
        }
      }, 0);
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => document.body.classList.remove('mobile-menu-open');
  }, [mobileMenuOpen]);

  // Restore sidebar scroll position
  useEffect(() => {
    const savedScrollPos = sessionStorage.getItem('sidebarScrollPos');
    if (savedScrollPos && sidebarRef.current) {
      sidebarRef.current.scrollTop = parseInt(savedScrollPos, 10);
    }
  }, []);

  // Save sidebar scroll position on scroll
  const handleSidebarScroll = () => {
    if (sidebarRef.current) {
      sessionStorage.setItem('sidebarScrollPos', sidebarRef.current.scrollTop.toString());
    }
  };
  
  // Fetch unread health alerts count (separate from blog/news notifications)
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/alerts/unread-count`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await response.json();
        setUnreadCount(data.count || 0);
      } catch (error) {
        console.error('Failed to fetch alert count:', error);
      }
    };
    
    if (user) {
      fetchUnreadCount();
      // Refresh every 60 seconds
      const interval = setInterval(fetchUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const [unreadBlogCount, setUnreadBlogCount] = useState(0);
  const [pendingArticlesCount, setPendingArticlesCount] = useState(0);

  // Fetch unread news count
  useEffect(() => {
    const fetchUnreadNewsCount = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/news/unread-count`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUnreadNewsCount(data.count || 0);
        }
      } catch (error) {
        console.error('Failed to fetch news count:', error);
      }
    };
    
    if (user) {
      fetchUnreadNewsCount();
    }
  }, [user]);

  // Fetch unread blog count
  useEffect(() => {
    const fetchUnreadBlogCount = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/unread-count`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUnreadBlogCount(data.count || 0);
        }
      } catch (error) {
        console.error('Failed to fetch blog count:', error);
      }
    };
    
    if (user) {
      fetchUnreadBlogCount();
    }
  }, [user]);

  // Fetch pending articles count for admins
  useEffect(() => {
    const fetchPendingArticlesCount = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/news/pending-count`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPendingArticlesCount(data.count || 0);
        }
      } catch (error) {
        // Silently fail for non-admins
      }
    };
    
    if (user?.role === 'admin') {
      fetchPendingArticlesCount();
    }
  }, [user]);

  // Badge resets are driven by the pages themselves (NewsPage / AdminNewsPage mark
  // read AFTER loading, so "New" highlights are computed before the reset)
  useEffect(() => {
    const onNewsRead = () => setUnreadNewsCount(0);
    const onAdminNewsViewed = () => setPendingArticlesCount(0);
    window.addEventListener('news-marked-read', onNewsRead);
    window.addEventListener('admin-news-viewed', onAdminNewsViewed);
    return () => {
      window.removeEventListener('news-marked-read', onNewsRead);
      window.removeEventListener('admin-news-viewed', onAdminNewsViewed);
    };
  }, []);

  const markBlogAsRead = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/mark-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setUnreadBlogCount(0);
    } catch (error) {
      console.error('Failed to mark blog as read:', error);
    }
  };

  const markAlertsAsRead = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/alerts/mark-all-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark alerts as read:', error);
    }
  };

  const markNewsCrawlerAsViewed = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/news/mark-viewed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setPendingArticlesCount(0);
    } catch (error) {
      console.error('Failed to mark news crawler as viewed:', error);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const navItems = [
    // === TRACK (Core health tracking) ===
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/blood-results', label: 'My Progress', icon: TrendingUp },
    { path: '/treatments', label: 'Treatments', icon: Pill },
    { path: '/nutrition', label: 'Nutrition & Deficiencies', icon: Apple },
    { path: '/symptoms', label: 'Symptoms', icon: FileText },
    
    // === READ (News, content, ideas) ===
    { path: '/news', label: 'CLL/SLL News', icon: Rss },
    { path: '/blog', label: 'Blog', icon: Newspaper },
    { path: '/my-ideas', label: 'My Ideas & Research', icon: Lightbulb },
    
    // === LEARN (Consolidated education) ===
    { 
      id: 'learn',
      path: '/learn', 
      label: 'Learn', 
      icon: GraduationCap,
      subItems: [
        { path: '/what-is-cll', label: 'What Is CLL/SLL' },
        { path: '/watch-and-wait', label: 'Watch and Wait' },
        { path: '/blood-markers', label: 'Blood Markers' },
        { path: '/glossary', label: 'Glossary' },
        { path: '/common-questions', label: 'Common Questions' },
        { path: '/reading-learning', label: 'Reading & Learning' },
        { path: '/updates-learning', label: 'Updates & Learning' }
      ]
    },
    
    // === INTEGRATIVE HEALTH ===
    { 
      id: 'integrative-health',
      path: '/integrative-health', 
      label: 'Integrative Health', 
      icon: Heart,
      subItems: [
        { path: '/vitamin-d-education', label: 'Vitamin D' },
        { path: '/stress-wellbeing', label: 'Stress & Mental Wellbeing' },
        { path: '/exercise-movement', label: 'Exercise & Movement' },
        { path: '/sleep-recovery', label: 'Sleep & Recovery' },
        { path: '/vitamin-c-iv', label: 'Vitamin C (IV)' },
        { path: '/green-tea-research', label: 'Green Tea (EGCG) Research' },
        { path: '/integrative-health/recipes', label: 'Recipes' },
        { path: '/integrative-health/smoothies', label: 'Smoothies' },
        { path: '/integrative-health/eating-well', label: 'Eating Well With CLL' },
        { path: '/integrative-health/food-safety', label: 'Food Safety' }
      ]
    },
    
    // === UTILITIES (Less frequent actions) ===
    { path: '/alerts', label: 'Alerts', icon: Bell },
    { path: '/export', label: 'Export', icon: Download },
    { path: '/how-to-use', label: 'How to Use', icon: Compass }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col h-full bg-white border-r border-slate-100 pt-6 pb-4">
          <div className="flex-shrink-0 px-6 mb-6">
            {/* Logo and Title */}
            <div className="flex items-center mb-1">
              <Activity className="h-8 w-8 text-blue-600" />
              <span className="ml-3 text-xl font-bold text-blue-600">MyCBC</span>
            </div>
            {/* Subtitle */}
            <p className="text-sm text-slate-500 ml-11">Your health tracking overview</p>
          </div>
          
          {/* Scrollable nav container */}
          <nav 
            ref={sidebarRef}
            onScroll={handleSidebarScroll}
            className="flex-1 px-4 space-y-1 overflow-y-auto"
          >
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const hasSubItems = item.subItems && item.subItems.length > 0;
              const isExpanded = expandedMenus.includes(item.id);
              const isSubItemActive = hasSubItems && item.subItems.some(sub => location.pathname === sub.path);
              const colors = getNavColors(item.path);
              
              return (
                <div key={item.path}>
                  {hasSubItems ? (
                    <>
                      {/* Parent item with sub-items - clicking anywhere expands submenu */}
                      <button
                        onClick={() => toggleMenu(item.id)}
                        data-testid={`nav-${item.path.slice(1)}`}
                        className={`w-full flex items-center px-4 py-3 text-base rounded-full transition-colors ${
                          isActive || isSubItemActive
                            ? 'font-medium'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }`}
                        style={(isActive || isSubItemActive) ? { backgroundColor: colors.activeBg } : {}}
                      >
                        <Icon 
                          className="h-5 w-5 mr-3" 
                          style={{ color: (isActive || isSubItemActive) ? colors.activeIcon : colors.icon }}
                        />
                        <span className={`flex-1 text-left ${(isActive || isSubItemActive) ? 'text-slate-900' : ''}`}>
                          {item.label}
                        </span>
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-slate-400" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        )}
                      </button>
                      {/* Sub-items */}
                      {isExpanded && (
                        <div className="ml-8 mt-1 flex flex-col gap-1">
                          {/* Overview link to main section page */}
                          <Link
                            to={item.path}
                            data-testid={`nav-${item.path.slice(1)}-overview`}
                            className={`flex items-center px-4 py-2 text-sm rounded-full transition-colors ${
                              isActive && !isSubItemActive
                                ? 'bg-primary/10 text-primary font-medium'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                            }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full mr-3 flex-shrink-0 ${isActive && !isSubItemActive ? 'bg-primary' : 'bg-slate-300'}`}></span>
                            Overview
                          </Link>
                          {item.subItems.map((subItem) => {
                            const isSubActive = location.pathname === subItem.path;
                            return (
                              <Link
                                key={subItem.path}
                                to={subItem.path}
                                data-testid={`nav-${subItem.path.slice(1)}`}
                                className={`flex items-center px-4 py-2 text-sm rounded-full transition-colors ${
                                  isSubActive
                                    ? 'bg-primary/10 text-primary font-medium'
                                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full mr-3 flex-shrink-0 ${isSubActive ? 'bg-primary' : 'bg-slate-300'}`}></span>
                                {subItem.label}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                      /* Regular item without sub-items */
                    <Link
                      to={item.path}
                      data-testid={`nav-${item.path.slice(1)}`}
                      onClick={item.path === '/blog' ? markBlogAsRead : item.path === '/alerts' ? markAlertsAsRead : undefined}
                      className={`flex items-center px-4 py-3 text-base rounded-full transition-colors ${
                        isActive
                          ? 'font-medium'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                      style={isActive ? { backgroundColor: colors.activeBg } : {}}
                    >
                      {item.emoji ? (
                        <span className="text-lg mr-3">{item.emoji}</span>
                      ) : (
                        <Icon 
                          className="h-5 w-5 mr-3" 
                          style={{ color: item.highlight ? '#0d9488' : (isActive ? colors.activeIcon : colors.icon) }}
                        />
                      )}
                      <span className={`flex-1 ${item.highlight ? 'text-primary font-semibold' : (isActive ? 'text-slate-900' : '')}`}>
                        {item.label}
                      </span>
                      {item.highlight && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
                          Soon
                        </span>
                      )}
                      {/* Notification badge for Alerts */}
                      {item.path === '/alerts' && unreadCount > 0 && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                      {/* Notification badge for Blog */}
                      {item.path === '/blog' && unreadBlogCount > 0 && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-primary text-white rounded-full">
                          {unreadBlogCount > 99 ? '99+' : unreadBlogCount}
                        </span>
                      )}
                      {/* Notification badge for CLL News */}
                      {item.path === '/news' && unreadNewsCount > 0 && (
                        <span data-testid="news-unread-badge" className="ml-2 px-2 py-0.5 text-xs font-bold bg-orange-500 text-white rounded-full">
                          {unreadNewsCount > 99 ? '99+' : unreadNewsCount}
                        </span>
                      )}
                    </Link>
                  )}
                </div>
              );
            })}
            
            {/* Admin Link - Only visible to admin users */}
            {user?.role === 'admin' && (
              <>
                <div className="my-2 border-t border-slate-200"></div>
                <Link
                  to="/admin"
                  className={`flex items-center px-4 py-3 text-base rounded-full transition-colors ${
                    location.pathname === '/admin'
                      ? 'font-medium'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                  style={location.pathname === '/admin' ? { backgroundColor: getNavColors('/admin').activeBg } : {}}
                >
                  <Shield 
                    className="h-5 w-5 mr-3" 
                    style={{ color: location.pathname === '/admin' ? getNavColors('/admin').activeIcon : getNavColors('/admin').icon }}
                  />
                  <span className={location.pathname === '/admin' ? 'text-slate-900' : ''}>
                    Admin Panel
                  </span>
                </Link>
                <Link
                  to="/admin/blog"
                  className={`flex items-center px-4 py-3 text-base rounded-full transition-colors ${
                    location.pathname === '/admin/blog'
                      ? 'font-medium bg-indigo-50'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Newspaper 
                    className="h-5 w-5 mr-3" 
                    style={{ color: location.pathname === '/admin/blog' ? '#4f46e5' : '#64748b' }}
                  />
                  <span className={location.pathname === '/admin/blog' ? 'text-slate-900' : ''}>
                    Blog Manager
                  </span>
                </Link>
                <Link
                  to="/admin/news"
                  onClick={markNewsCrawlerAsViewed}
                  className={`flex items-center px-4 py-3 text-base rounded-full transition-colors ${
                    location.pathname === '/admin/news'
                      ? 'font-medium bg-indigo-50'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Rss 
                    className="h-5 w-5 mr-3" 
                    style={{ color: location.pathname === '/admin/news' ? '#4f46e5' : '#64748b' }}
                  />
                  <span className={`flex-1 ${location.pathname === '/admin/news' ? 'text-slate-900' : ''}`}>
                    News Crawler
                  </span>
                  {pendingArticlesCount > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
                      {pendingArticlesCount > 99 ? '99+' : pendingArticlesCount}
                    </span>
                  )}
                </Link>
                <Link
                  to="/admin/roadmap"
                  className={`flex items-center px-4 py-3 text-base rounded-full transition-colors ${
                    location.pathname === '/admin/roadmap'
                      ? 'font-medium bg-indigo-50'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Map 
                    className="h-5 w-5 mr-3" 
                    style={{ color: location.pathname === '/admin/roadmap' ? '#4f46e5' : '#64748b' }}
                  />
                  <span className={location.pathname === '/admin/roadmap' ? 'text-slate-900' : ''}>
                    Internal Roadmap
                  </span>
                </Link>
              </>
            )}
          </nav>
          
          <div className="px-4 pt-4 border-t border-slate-100">
            <div className="px-4 py-3 mb-2 rounded-xl bg-slate-50">
              <p className="text-sm font-medium text-slate-900">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
            <Link
              to="/profile"
              className="flex items-center w-full px-4 py-3 mb-1 text-base text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-full transition-colors"
            >
              <User className="h-5 w-5 mr-3" />
              Profile
            </Link>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center w-full px-4 py-3 text-base text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-full transition-colors"
            >
              <LogOut className="h-5 w-5 mr-3" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header - Higher z-index than menu to stay on top */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-slate-100 z-[60]">
        <div className="px-4 py-2">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="p-1.5 rounded-lg hover:bg-slate-50"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex items-center">
              <Activity className="h-5 w-5 text-blue-600" />
              <span className="ml-1.5 text-base font-bold text-blue-600">MyCBC</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div ref={mobileMenuRef} className="lg:hidden fixed inset-0 z-[55] bg-white pt-12 overflow-y-auto">
          <nav className="px-4 py-6 space-y-1 pb-24">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const hasSubItems = item.subItems && item.subItems.length > 0;
              const isExpanded = expandedMenus.includes(item.id);
              const isSubItemActive = hasSubItems && item.subItems.some(sub => location.pathname === sub.path);
              const colors = getNavColors(item.path);
              
              return (
                <div key={item.path}>
                  {hasSubItems ? (
                    <>
                      {/* Parent item with sub-items - Mobile - clicking expands submenu */}
                      <button
                        onClick={() => toggleMenu(item.id)}
                        className={`w-full flex items-center px-4 py-3 text-lg rounded-full transition-colors ${
                          isActive || isSubItemActive
                            ? 'font-medium'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                        style={(isActive || isSubItemActive) ? { backgroundColor: colors.activeBg } : {}}
                      >
                        <Icon 
                          className="h-5 w-5 mr-3" 
                          style={{ color: (isActive || isSubItemActive) ? colors.activeIcon : colors.icon }}
                        />
                        <span className={`flex-1 text-left ${(isActive || isSubItemActive) ? 'text-slate-900' : ''}`}>
                          {item.label}
                        </span>
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-slate-400" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        )}
                      </button>
                      {/* Sub-items - Mobile */}
                      {isExpanded && (
                        <div className="ml-8 mt-1 flex flex-col gap-1">
                          {/* Overview link to main section page */}
                          <Link
                            to={item.path}
                            onClick={closeMobileMenu}
                            className={`flex items-center px-4 py-2.5 text-base rounded-full transition-colors ${
                              isActive && !isSubItemActive
                                ? 'bg-primary/10 text-primary font-medium'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                            }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full mr-3 flex-shrink-0 ${isActive && !isSubItemActive ? 'bg-primary' : 'bg-slate-300'}`}></span>
                            Overview
                          </Link>
                          {item.subItems.map((subItem) => {
                            const isSubActive = location.pathname === subItem.path;
                            return (
                              <Link
                                key={subItem.path}
                                to={subItem.path}
                                onClick={closeMobileMenu}
                                className={`flex items-center px-4 py-2.5 text-base rounded-full transition-colors ${
                                  isSubActive
                                    ? 'bg-primary/10 text-primary font-medium'
                                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full mr-3 flex-shrink-0 ${isSubActive ? 'bg-primary' : 'bg-slate-300'}`}></span>
                                {subItem.label}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    /* Regular item without sub-items - Mobile */
                    <Link
                      to={item.path}
                      onClick={() => {
                        closeMobileMenu();
                        if (item.path === '/blog') markBlogAsRead();
                        if (item.path === '/alerts') markAlertsAsRead();
                      }}
                      className={`flex items-center px-4 py-3 text-lg rounded-full transition-colors ${
                        isActive
                          ? 'font-medium'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                      style={isActive ? { backgroundColor: colors.activeBg } : {}}
                    >
                      {item.emoji ? (
                        <span className="text-lg mr-3">{item.emoji}</span>
                      ) : (
                        <Icon 
                          className="h-5 w-5 mr-3" 
                          style={{ color: item.highlight ? '#0d9488' : (isActive ? colors.activeIcon : colors.icon) }}
                        />
                      )}
                      <span className={`flex-1 ${item.highlight ? 'text-primary font-semibold' : (isActive ? 'text-slate-900' : '')}`}>
                        {item.label}
                      </span>
                      {/* Notification badge for Alerts - Mobile */}
                      {item.path === '/alerts' && unreadCount > 0 && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                      {/* Notification badge for Blog - Mobile */}
                      {item.path === '/blog' && unreadBlogCount > 0 && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-primary text-white rounded-full">
                          {unreadBlogCount > 99 ? '99+' : unreadBlogCount}
                        </span>
                      )}
                      {/* Notification badge for CLL News - Mobile */}
                      {item.path === '/news' && unreadNewsCount > 0 && (
                        <span data-testid="news-unread-badge-mobile" className="ml-2 px-2 py-0.5 text-xs font-bold bg-orange-500 text-white rounded-full">
                          {unreadNewsCount > 99 ? '99+' : unreadNewsCount}
                        </span>
                      )}
                    </Link>
                  )}
                </div>
              );
            })}
            
            {/* Admin Link - Mobile - Only visible to admin users */}
            {user?.role === 'admin' && (
              <>
                <div className="my-2 border-t border-slate-200"></div>
                <Link
                  to="/admin"
                  onClick={closeMobileMenu}
                  className={`flex items-center px-4 py-3 text-lg rounded-full transition-colors ${
                    location.pathname === '/admin'
                      ? 'font-medium'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                  style={location.pathname === '/admin' ? { backgroundColor: getNavColors('/admin').activeBg } : {}}
                >
                  <Shield 
                    className="h-5 w-5 mr-3" 
                    style={{ color: location.pathname === '/admin' ? getNavColors('/admin').activeIcon : getNavColors('/admin').icon }}
                  />
                  <span className={location.pathname === '/admin' ? 'text-slate-900' : ''}>
                    Admin Panel
                  </span>
                </Link>
                <Link
                  to="/admin/blog"
                  onClick={closeMobileMenu}
                  className={`flex items-center px-4 py-3 text-lg rounded-full transition-colors ${
                    location.pathname === '/admin/blog'
                      ? 'font-medium bg-indigo-50'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Newspaper 
                    className="h-5 w-5 mr-3" 
                    style={{ color: location.pathname === '/admin/blog' ? '#4f46e5' : '#64748b' }}
                  />
                  <span className={location.pathname === '/admin/blog' ? 'text-slate-900' : ''}>
                    Blog Manager
                  </span>
                </Link>
                <Link
                  to="/admin/news"
                  onClick={() => { closeMobileMenu(); markNewsCrawlerAsViewed(); }}
                  className={`flex items-center px-4 py-3 text-lg rounded-full transition-colors ${
                    location.pathname === '/admin/news'
                      ? 'font-medium bg-indigo-50'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Rss 
                    className="h-5 w-5 mr-3" 
                    style={{ color: location.pathname === '/admin/news' ? '#4f46e5' : '#64748b' }}
                  />
                  <span className={`flex-1 ${location.pathname === '/admin/news' ? 'text-slate-900' : ''}`}>
                    News Crawler
                  </span>
                  {pendingArticlesCount > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
                      {pendingArticlesCount > 99 ? '99+' : pendingArticlesCount}
                    </span>
                  )}
                </Link>
                <Link
                  to="/admin/roadmap"
                  onClick={closeMobileMenu}
                  className={`flex items-center px-4 py-3 text-lg rounded-full transition-colors ${
                    location.pathname === '/admin/roadmap'
                      ? 'font-medium bg-indigo-50'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Map 
                    className="h-5 w-5 mr-3" 
                    style={{ color: location.pathname === '/admin/roadmap' ? '#4f46e5' : '#64748b' }}
                  />
                  <span className={location.pathname === '/admin/roadmap' ? 'text-slate-900' : ''}>
                    Internal Roadmap
                  </span>
                </Link>
              </>
            )}
            
            <div className="pt-6 mt-6 border-t border-slate-100">
              <div className="px-4 py-3 mb-4 rounded-xl bg-slate-50">
                <p className="text-base font-medium text-slate-900">{user?.name}</p>
                <p className="text-sm text-slate-500">{user?.email}</p>
              </div>
              <Link
                to="/profile"
                onClick={closeMobileMenu}
                className="flex items-center w-full px-4 py-3 mb-2 text-lg text-slate-600 hover:bg-slate-50 rounded-full transition-colors"
              >
                <User className="h-5 w-5 mr-3" />
                Profile
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-3 text-lg text-slate-600 hover:bg-slate-50 rounded-full transition-colors"
              >
                <LogOut className="h-5 w-5 mr-3" />
                Logout
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <div className="lg:pl-72">
        <main className={`py-6 px-4 md:px-8 lg:px-12 ${isReadOnly ? 'pt-24 lg:pt-24' : 'pt-14 lg:pt-6'} min-h-screen`}>
          {children}
        </main>
        <Footer />
      </div>

      {/* Share MyCBC Prompt - shows after 7 logins */}
      <ShareMyCBCModal
        isOpen={showSharePrompt}
        onClose={() => setShowSharePrompt(false)}
        onDismiss={handleDismissSharePrompt}
        showDismissOption={true}
      />
    </div>
  );
};

export default Layout;